/**
 * The tie-in to the Medicaid Enrollment / Re-determination program.
 *
 * Lines captured anywhere in the registry are matched to this org's enrollments
 * (exact name and date of birth; an ambiguous match is left alone). A month's
 * counted lines are then delivered into the enrollment's Medicaid work record —
 * the same work_activity_logs row the work-compliance dashboards read — and the
 * open redetermination is marked as having its work-hours evidence once every
 * lookback month is in.
 *
 * Exemptions: staff record what the member reported and start evidence
 * collection. The determination is the state's; nothing here marks anyone exempt.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle2, AlertCircle, GitMerge, Plus, RefreshCw, Send, Search } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { PageState } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  EXEMPTION_TYPES,
  type EnrollmentDetail,
  type EnrollmentRow,
  type MemberHit,
  verifyApi,
} from './api';
import {
  ErrorNote,
  Field,
  HoursBar,
  NativeSelect,
  RecordStatusBadge,
  StatementStateBadge,
  activityLabel,
  errMessage,
  fmtDate,
  fmtHours,
  humanize,
  inputClass,
  missingFieldLabel,
  monthName,
  shortMonth,
  sourceLabel,
} from './shared';

function recentMonths(count: number): { year: number; month: number }[] {
  const now = new Date();
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return out;
}

export function VerifyMedicaid() {
  const { t, language } = useAppTranslation();
  const months = useMemo(() => recentMonths(12), []);
  const [period, setPeriod] = useState(months[0]);
  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matchNote, setMatchNote] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows((await verifyApi.enrollments(period.year, period.month)).enrollments);
    } catch (e) {
      setError(errMessage(e, t('verify.medicaid.load_failed', 'Could not load enrollments.')));
    } finally {
      setLoading(false);
    }
  }, [period, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runMatch = async () => {
    setMatching(true);
    setMatchNote(null);
    setError(null);
    try {
      const r = await verifyApi.match();
      setMatchNote(
        t('verify.medicaid.match_result', '{{matched}} lines matched to enrollments. {{ambiguous}} matched more than one person and were left for review.', {
          matched: r.matched,
          ambiguous: r.ambiguous,
        }),
      );
      await load();
    } catch (e) {
      setError(errMessage(e, t('verify.medicaid.match_failed', 'Matching failed.')));
    } finally {
      setMatching(false);
    }
  };

  if (openId !== null) {
    return (
      <EnrollmentView
        id={openId}
        initial={period}
        months={months}
        onBack={() => {
          setOpenId(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('verify.medicaid.month', 'Month')}>
            <NativeSelect
              value={`${period.year}-${period.month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setPeriod({ year: y, month: m });
              }}
            >
              {months.map((m) => (
                <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                  {monthName(m.year, m.month, language)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('verify.refresh', 'Refresh')}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void runMatch()} disabled={matching}>
            <GitMerge className="mr-2 h-4 w-4" />
            {matching ? t('verify.medicaid.matching', 'Matching…') : t('verify.medicaid.match', 'Match records')}
          </Button>
          <Button size="sm" onClick={() => setTracking(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('verify.medicaid.track', 'Track a member')}
          </Button>
        </div>
      </div>

      <p className="max-w-3xl text-sm text-ink-500">
        {t(
          'verify.medicaid.intro',
          'Members on the Medicaid Enrollment / Re-determination program with the work requirement. Match pulls in lines recorded at any qualified organization; Deliver writes the month into the Medicaid work record.',
        )}
      </p>

      {matchNote && (
        <div className="rounded-md bg-v2accent-soft px-3 py-2 text-sm text-v2accent-ink">{matchNote}</div>
      )}
      <ErrorNote message={error} />

      {loading && rows.length === 0 ? (
        <PageState variant="loading" />
      ) : rows.length === 0 ? (
        <PageState
          variant="empty"
          title={t('verify.medicaid.empty_title', 'No one is tracked yet')}
          message={t('verify.medicaid.empty', 'Track a member to put them on the Medicaid Enrollment / Re-determination program with the work requirement.')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-v2border text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('verify.medicaid.member', 'Member')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.medicaid.requirement', 'Work requirement')}</th>
                    <th className="w-[260px] px-4 py-2 font-medium">{t('verify.medicaid.hours', 'Hours this month')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.medicaid.state', 'State')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.medicaid.delivered', 'Delivered')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.medicaid.redetermination', 'Redetermination')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr
                      key={e.id}
                      className="cursor-pointer border-b border-v2border last:border-0 hover:bg-paper-2"
                      onClick={() => setOpenId(e.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink">
                          {e.first_name} {e.last_name}
                        </div>
                        <div className="text-xs text-ink-500">{e.program_name || e.enrollment_number}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{humanize(e.work_requirement_status)}</td>
                      <td className="px-4 py-3">
                        <div className="mb-1 flex justify-between text-xs text-ink-500">
                          <span>
                            {fmtHours(e.statement.counted_hours)} / {fmtHours(e.statement.required_hours)}
                          </span>
                          {e.statement.pending_hours > 0 && (
                            <span>
                              +{fmtHours(e.statement.pending_hours)} {t('verify.medicaid.pending', 'pending')}
                            </span>
                          )}
                        </div>
                        <HoursBar
                          counted={e.statement.counted_hours}
                          verified={e.statement.verified_hours}
                          pending={e.statement.pending_hours}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <StatementStateBadge state={e.statement.state} t={t} />
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {e.delivered ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4 text-success" />
                            {fmtHours(e.delivered.total_hours)}
                          </span>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-700">{fmtDate(e.next_redetermination_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <TrackDialog
        open={tracking}
        onClose={() => setTracking(false)}
        onTracked={(id) => {
          setTracking(false);
          setOpenId(id);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Track a member
// ---------------------------------------------------------------------------

function TrackDialog({ open, onClose, onTracked }: { open: boolean; onClose: () => void; onTracked: (id: number) => void }) {
  const { t } = useAppTranslation();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<MemberHit[]>([]);
  const [picked, setPicked] = useState<MemberHit | null>(null);
  const [redet, setRedet] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHits([]);
      setPicked(null);
      setRedet('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = window.setTimeout(() => {
      verifyApi
        .members(query)
        .then((r) => setHits(r.members))
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(h);
  }, [query, open]);

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const e = await verifyApi.trackMember(picked.id, redet || undefined);
      onTracked(e.id);
    } catch (err) {
      setError(errMessage(err, t('verify.medicaid.track_failed', 'Could not track this member.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('verify.medicaid.track_title', 'Track a member')}</DialogTitle>
          <DialogDescription>
            {t(
              'verify.medicaid.track_desc',
              'Puts the member on the Medicaid Enrollment / Re-determination program with the work requirement applying. A member already enrolled here keeps their enrollment.',
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-ink-400" />
            <input
              className={`${inputClass} pl-9`}
              placeholder={t('verify.capture.search_member', 'Search your members…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {hits.map((h) => (
              <button
                key={h.id}
                type="button"
                disabled={!h.has_dob}
                onClick={() => setPicked(h)}
                className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm ${
                  picked?.id === h.id ? 'bg-v2accent-soft text-v2accent-ink' : 'hover:bg-paper-2'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span>
                  {h.first_name} {h.last_name}
                </span>
                <span className="text-xs text-ink-500">
                  {h.enrollment_id
                    ? t('verify.medicaid.already_tracked', 'already tracked')
                    : !h.has_dob
                      ? t('verify.medicaid.needs_dob', 'needs a date of birth')
                      : ''}
                </span>
              </button>
            ))}
          </div>
          <Field
            label={t('verify.medicaid.redet_date', 'Next redetermination (optional)')}
            hint={t('verify.medicaid.redet_hint', 'Defaults to six months from now.')}
          >
            <input className={inputClass} type="date" value={redet} onChange={(e) => setRedet(e.target.value)} />
          </Field>
        </div>
        <ErrorNote message={error} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('verify.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={!picked || busy}>
            {busy ? t('verify.saving', 'Saving…') : t('verify.medicaid.track', 'Track a member')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// One enrollment
// ---------------------------------------------------------------------------

function EnrollmentView({
  id,
  initial,
  months,
  onBack,
}: {
  id: number;
  initial: { year: number; month: number };
  months: { year: number; month: number }[];
  onBack: () => void;
}) {
  const { t, language } = useAppTranslation();
  const [period, setPeriod] = useState(initial);
  const [data, setData] = useState<EnrollmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [due, setDue] = useState('');
  const [exType, setExType] = useState<string>(EXEMPTION_TYPES[0]);
  const [exReason, setExReason] = useState('');
  const [screening, setScreening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await verifyApi.enrollment(id, period.year, period.month));
    } catch (e) {
      setError(errMessage(e, t('verify.medicaid.detail_failed', 'Could not load this enrollment.')));
    } finally {
      setLoading(false);
    }
  }, [id, period, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (key: string, fn: () => Promise<EnrollmentDetail>) => {
    setBusy(key);
    setError(null);
    try {
      setData(await fn());
      return true;
    } catch (e) {
      setError(errMessage(e, t('verify.medicaid.action_failed', 'That did not work.')));
      return false;
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) return <PageState variant="loading" />;
  if (!data) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('verify.back', 'Back')}
        </Button>
        <ErrorNote message={error} />
      </div>
    );
  }

  const { enrollment: e, statement: st, readiness, exemptions } = data;
  const lines = st.lines || [];
  const canDeliver = st.counted_lines > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('verify.back', 'Back')}
          </Button>
          <div>
            <div className="font-display text-lg font-semibold text-ink">
              {e.first_name} {e.last_name}
            </div>
            <div className="text-xs text-ink-500">
              {[e.program_name, e.enrollment_number, humanize(e.status)].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={data.exempt ? 'info' : e.work_requirement_status === 'COMPLIANT' ? 'success' : 'warning'} size="sm">
            {data.exempt ? t('verify.medicaid.exempt', 'Exempt') : humanize(e.work_requirement_status)}
          </Badge>
          <NativeSelect
            value={`${period.year}-${period.month}`}
            onChange={(ev) => {
              const [y, m] = ev.target.value.split('-').map(Number);
              setPeriod({ year: y, month: m });
            }}
            className="w-auto"
          >
            {months.map((m) => (
              <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {monthName(m.year, m.month, language)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      <ErrorNote message={error} />

      {/* History strip */}
      <div className="grid grid-cols-6 gap-2">
        {data.history.map((h) => {
          const active = h.year === period.year && h.month === period.month;
          return (
            <button
              key={`${h.year}-${h.month}`}
              type="button"
              onClick={() => setPeriod({ year: h.year, month: h.month })}
              className={`rounded-md border px-2 py-2 text-left ${active ? 'border-v2accent bg-v2accent-soft' : 'border-v2border bg-surface'}`}
            >
              <div className="text-xs uppercase text-ink-500">{shortMonth(h.year, h.month, language)}</div>
              <div className="font-mono text-sm text-ink">{Math.round(h.counted_hours)} h</div>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-ink-500">
                {h.meets_requirement ? (
                  <CheckCircle2 className="h-3 w-3 text-success" />
                ) : (
                  <AlertCircle className="h-3 w-3 text-ink-300" />
                )}
                {h.delivered ? t('verify.medicaid.sent', 'sent') : ''}
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* Statement */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">
                {t('verify.medicaid.statement', 'Statement for {{month}}', { month: monthName(st.year, st.month, language) })}
              </CardTitle>
              <StatementStateBadge state={st.state} t={t} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.exempt && (
              <div className="rounded-md bg-v2accent-soft px-3 py-2 text-sm text-v2accent-ink">
                {t('verify.medicaid.exempt_note', 'The state has this member as exempt. Hours are kept, but not required.')}
              </div>
            )}
            <div>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="font-display text-2xl font-semibold text-ink">
                  {fmtHours(st.counted_hours)}
                  <span className="ml-1 text-sm font-normal text-ink-500">/ {fmtHours(st.required_hours)}</span>
                </span>
                <span className="text-xs text-ink-500">
                  {st.days_until_deadline >= 0
                    ? t('verify.medicaid.days_left', '{{n}} days left in the month', { n: st.days_until_deadline })
                    : t('verify.medicaid.month_closed', 'Month closed')}
                </span>
              </div>
              <HoursBar counted={st.counted_hours} verified={st.verified_hours} pending={st.pending_hours} />
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-700">
                <span>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-v2accent" />
                  {t('verify.medicaid.confirmed_by_org', 'Confirmed by an organization')}: {fmtHours(st.verified_hours)}
                </span>
                <span>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-v2accent-soft ring-1 ring-v2accent" />
                  {t('verify.medicaid.self_reported', 'Self-reported')}: {fmtHours(st.self_attested_hours)}
                </span>
                <span>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-attention-soft ring-1 ring-attention" />
                  {t('verify.medicaid.awaiting', 'Awaiting confirmation')}: {fmtHours(st.pending_hours)}
                </span>
                {st.disputed_hours > 0 && (
                  <span className="text-danger-ink">
                    {t('verify.medicaid.disputed', 'Disputed')}: {fmtHours(st.disputed_hours)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {Object.entries(st.hours_by_type)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => (
                  <span key={k} className="rounded-full border border-v2border px-2.5 py-0.5 text-xs text-ink-700">
                    {activityLabel(t, k)} · {fmtHours(v)}
                  </span>
                ))}
            </div>

            {st.incomplete_lines > 0 && (
              <div className="rounded-md bg-attention-soft px-3 py-2 text-sm text-attention-ink">
                {t(
                  'verify.medicaid.incomplete',
                  '{{n}} counted lines are missing one of the four federal oversight fields (type of service, date, hours, point of contact).',
                  { n: st.incomplete_lines },
                )}
              </div>
            )}

            <div className="overflow-x-auto rounded-md border border-v2border">
              <table className="w-full text-sm">
                <thead className="border-b border-v2border bg-paper-2 text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('verify.date', 'Date')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.service', 'Type of service')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('verify.hours', 'Hours')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.where', 'Where · contact')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.filed', 'Filed')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-4 text-center text-ink-500">
                        {t('verify.medicaid.no_lines', 'No lines this month. Try Match records on the list.')}
                      </td>
                    </tr>
                  ) : (
                    lines.map((l) => (
                      <tr key={l.id} className="border-b border-v2border align-top last:border-0">
                        <td className="whitespace-nowrap px-3 py-2 text-ink-700">{fmtDate(l.activity_date)}</td>
                        <td className="px-3 py-2">
                          <div className="text-ink">{l.service_description}</div>
                          <div className="text-xs text-ink-500">{activityLabel(t, l.activity_type)}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{fmtHours(l.hours)}</td>
                        <td className="px-3 py-2 text-ink-700">
                          <div>{l.site_name || '—'}</div>
                          <div className="text-xs text-ink-500">{l.point_of_contact_name || '—'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-ink-700">{sourceLabel(t, l.source)}</div>
                          <div className="text-xs text-ink-500">{l.filed_by_name || l.filed_by_label}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col items-start gap-1">
                            <RecordStatusBadge status={l.status} t={t} />
                            {l.missing_fields.map((m) => (
                              <Badge key={m} variant="warning" size="sm">
                                {missingFieldLabel(t, m)}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-v2border pt-3">
              <div className="text-sm text-ink-700">
                {st.delivered ? (
                  st.delivery_stale ? (
                    <span className="text-attention-ink">
                      {t(
                        'verify.medicaid.stale',
                        'Delivered {{hours}} on {{date}}, but the lines have changed since. Deliver again to update the Medicaid record.',
                        { hours: fmtHours(st.delivered.total_hours), date: fmtDate(st.delivered.delivered_at) },
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-success-ink">
                      <CheckCircle2 className="h-4 w-4" />
                      {t('verify.medicaid.delivered_on', 'Delivered {{hours}} to the Medicaid work record on {{date}}', {
                        hours: fmtHours(st.delivered.total_hours),
                        date: fmtDate(st.delivered.delivered_at),
                      })}
                    </span>
                  )
                ) : (
                  <span className="text-ink-500">
                    {t('verify.medicaid.not_delivered', 'Not delivered yet. Delivering writes the counted lines into the Medicaid work record for this month.')}
                  </span>
                )}
              </div>
              <Button
                onClick={() => void act('deliver', () => verifyApi.deliver(id, period.year, period.month))}
                disabled={!canDeliver || busy !== null}
                variant={st.delivered && !st.delivery_stale ? 'outline' : 'primary'}
              >
                <Send className="mr-2 h-4 w-4" />
                {busy === 'deliver'
                  ? t('verify.medicaid.delivering', 'Delivering…')
                  : st.delivered
                    ? t('verify.medicaid.redeliver', 'Deliver again')
                    : t('verify.medicaid.deliver', 'Deliver month')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Redetermination readiness */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">{t('verify.medicaid.readiness', 'Redetermination readiness')}</CardTitle>
                <Badge variant={readiness.ready ? 'success' : 'warning'} size="sm">
                  {readiness.ready ? t('verify.medicaid.ready', 'Evidence complete') : t('verify.medicaid.not_ready', 'Evidence incomplete')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-ink-700">
                {readiness.redetermination
                  ? t('verify.medicaid.redet_open', 'Redetermination #{{n}} is open ({{status}}), due {{date}}.', {
                      n: readiness.redetermination.redetermination_number,
                      status: humanize(readiness.redetermination.status).toLowerCase(),
                      date: fmtDate(readiness.redetermination.due_date),
                    })
                  : readiness.due_date
                    ? t('verify.medicaid.redet_next', 'Next redetermination due {{date}}. None is open yet.', {
                        date: fmtDate(readiness.due_date),
                      })
                    : t('verify.medicaid.redet_none', 'No redetermination date on file.')}
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wide text-ink-500">
                  {t('verify.medicaid.lookback', 'Lookback months')}
                </div>
                {readiness.lookback_months.map((m) => (
                  <div key={`${m.year}-${m.month}`} className="flex items-center justify-between rounded border border-v2border px-2 py-1.5 text-sm">
                    <span className="text-ink">{monthName(m.year, m.month, language)}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs text-ink-700">{fmtHours(m.counted_hours)}</span>
                      {m.delivered && !m.delivery_stale ? (
                        <Badge variant="success" size="sm">
                          {t('verify.medicaid.sent', 'sent')}
                        </Badge>
                      ) : m.delivery_stale ? (
                        <Badge variant="warning" size="sm">
                          {t('verify.medicaid.resend', 'resend')}
                        </Badge>
                      ) : (
                        <Badge variant="muted" size="sm">
                          {t('verify.medicaid.not_sent', 'not sent')}
                        </Badge>
                      )}
                      {m.meets_requirement ? (
                        <CheckCircle2 className="h-4 w-4 text-success" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-attention" />
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {readiness.redetermination?.work_hours_verified && (
                <div className="text-sm text-success-ink">
                  {t('verify.medicaid.redet_verified', 'Work hours marked verified on the redetermination {{date}}.', {
                    date: fmtDate(readiness.redetermination.work_verification_date),
                  })}
                </div>
              )}
              {!readiness.redetermination && (
                <div className="flex flex-wrap items-end gap-2 border-t border-v2border pt-3">
                  <Field label={t('verify.medicaid.due_date', 'Due date')}>
                    <input
                      className={inputClass}
                      type="date"
                      value={due || readiness.due_date || ''}
                      onChange={(ev) => setDue(ev.target.value)}
                    />
                  </Field>
                  <Button
                    variant="outline"
                    onClick={() => void act('redet', () => verifyApi.openRedetermination(id, due || readiness.due_date || undefined))}
                    disabled={busy !== null || !(due || readiness.due_date)}
                  >
                    {t('verify.medicaid.open_redet', 'Open redetermination')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Exemptions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('verify.medicaid.exemptions', 'Exemption screening')}</CardTitle>
              <p className="text-xs text-ink-500">
                {t(
                  'verify.medicaid.exemptions_note',
                  'Screen for an exemption before asking for hours. WeaveIn records what the member reported and tracks the evidence; the state decides.',
                )}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {exemptions.length === 0 ? (
                <p className="text-sm text-ink-500">{t('verify.medicaid.no_exemptions', 'Nothing screened yet.')}</p>
              ) : (
                exemptions.map((x) => (
                  <div key={x.id} className="rounded border border-v2border px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-ink">{humanize(x.exemption_type)}</span>
                      <Badge variant={x.status === 'APPROVED' ? 'success' : x.status === 'DENIED' ? 'danger' : 'warning'} size="sm">
                        {x.status === 'PENDING' ? t('verify.medicaid.pending_state', 'Pending state review') : humanize(x.status)}
                      </Badge>
                    </div>
                    {x.exemption_reason && <div className="text-ink-700">{x.exemption_reason}</div>}
                    <div className="text-xs text-ink-500">
                      {fmtDate(x.created_at)}
                      {x.documentation_required &&
                        ` · ${x.documentation_received ? t('verify.medicaid.docs_in', 'documents received') : t('verify.medicaid.docs_needed', 'documents needed')}`}
                    </div>
                  </div>
                ))
              )}
              {screening ? (
                <div className="space-y-2 border-t border-v2border pt-3">
                  <Field label={t('verify.medicaid.ex_type', 'What the member reported')}>
                    <NativeSelect value={exType} onChange={(ev) => setExType(ev.target.value)}>
                      {EXEMPTION_TYPES.map((x) => (
                        <option key={x} value={x}>
                          {humanize(x)}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                  <Field label={t('verify.medicaid.ex_reason', 'In their words')}>
                    <textarea className={`${inputClass} min-h-[64px]`} value={exReason} onChange={(ev) => setExReason(ev.target.value)} />
                  </Field>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setScreening(false)}>
                      {t('verify.cancel', 'Cancel')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy !== null || exReason.trim().length < 3}
                      onClick={async () => {
                        const ok = await act('exempt', () => verifyApi.screenExemption(id, exType, exReason.trim()));
                        if (ok) {
                          setScreening(false);
                          setExReason('');
                        }
                      }}
                    >
                      {t('verify.medicaid.record_screen', 'Record and start evidence')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setScreening(true)}>
                  {t('verify.medicaid.screen', 'Screen for an exemption')}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
